/**
 * STAGE 04 — cardinal ORIENTATION (port of orient.py).
 *
 * Decide which of 0/90/180/270 is upright:
 *   a. FACE (primary): a frontal detector (YuNet) only fires on an upright face,
 *      so rotate to each of the 4 poses and take the strongest face. conf ≥ 0.80
 *      → method "face".
 *   b. OSD (fallback): Tesseract --psm 0 reads text orientation; orientation_conf
 *      ≥ 1.0 → method "osd".
 *   c. Otherwise leave as-is → method "none".
 *
 * The face "strength" score is conf · sqrt(area), matching _face_strength.
 * A MANUAL ROTATION override is applied on top by the pipeline (not here).
 */
import { ImagePixels } from '../../core/ports';
import { rotate90, resizeImage } from '../core/image';
import { FaceBox, FaceDetector, OsdDetector } from '../ports';

export type OrientMethod = 'face' | 'osd' | 'none';

const FACE_CONF = 0.8;
const OSD_MIN_CONF = 1.0;

/**
 * Port of largest_face: run the detector on a ≤1024px-long-side copy and return
 * the most-confident face in the ORIGINAL image's coordinate space.
 */
export async function largestFace(img: ImagePixels, det: FaceDetector): Promise<FaceBox | null> {
  const longest = Math.max(img.width, img.height);
  const scale = longest > 1024 ? 1024 / longest : 1.0;
  const scaled =
    scale === 1.0 ? img : resizeImage(img, img.width * scale, img.height * scale);
  const faces = await det.detect(scaled);
  if (!faces.length) return null;
  let best: FaceBox | null = null;
  for (const f of faces) {
    if (!best || f.conf > best.conf) {
      best = { x: f.x / scale, y: f.y / scale, w: f.w / scale, h: f.h / scale, conf: f.conf };
    }
  }
  return best;
}

/** Port of _face_strength: best (conf·sqrt(area), conf) over detected faces. */
async function faceStrength(img: ImagePixels, det: FaceDetector): Promise<{ strength: number; conf: number }> {
  const longest = Math.max(img.width, img.height);
  const scale = longest > 1024 ? 1024 / longest : 1.0;
  const scaled =
    scale === 1.0 ? img : resizeImage(img, img.width * scale, img.height * scale);
  const faces = await det.detect(scaled);
  if (!faces.length) return { strength: 0, conf: 0 };
  let bestS = 0, bestC = 0;
  for (const f of faces) {
    const s = f.conf * Math.sqrt(Math.max(f.w * f.h, 1));
    if (s > bestS) {
      bestS = s;
      bestC = f.conf;
    }
  }
  return { strength: bestS, conf: bestC };
}

/** Port of detect_cardinal: (kCw degrees to upright, method). */
export async function detectCardinal(
  img: ImagePixels,
  face: FaceDetector | undefined,
  osd: OsdDetector | undefined,
): Promise<{ k: 0 | 90 | 180 | 270; method: OrientMethod }> {
  if (face) {
    const ks: (0 | 90 | 180 | 270)[] = [0, 90, 180, 270];
    const scored: { k: 0 | 90 | 180 | 270; strength: number; conf: number }[] = [];
    for (const k of ks) {
      const rotated = rotate90(img, k);
      const { strength, conf } = await faceStrength(rotated, face);
      scored.push({ k, strength, conf });
    }
    scored.sort((a, b) => b.strength - a.strength);
    if (scored[0].conf >= FACE_CONF) return { k: scored[0].k, method: 'face' };
  }

  if (osd) {
    try {
      const res = await osd.detect(img);
      if (res && res.orientationConf >= OSD_MIN_CONF) {
        const k = (((res.rotate % 360) + 360) % 360) as 0 | 90 | 180 | 270;
        return { k, method: 'osd' };
      }
    } catch {
      // OSD best-effort; fall through to "none".
    }
  }
  return { k: 0, method: 'none' };
}

/** Port of orient_upright: rotate to upright, returning (image, method). */
export async function orientUpright(
  img: ImagePixels,
  face: FaceDetector | undefined,
  osd: OsdDetector | undefined,
): Promise<{ image: ImagePixels; method: OrientMethod }> {
  const { k, method } = await detectCardinal(img, face, osd);
  return { image: rotate90(img, k), method };
}
